import React, { useCallback, useEffect, useState } from 'react';
import { Skeleton, message } from 'antd';
import styled from '@emotion/styled';
import { isEmpty, maxBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { PER_PAGE_100 } from '../../../constants/common';
import {
  getTourStoryListRepository,
  postCreateTourStoryRepository,
} from '../../../repository/mytm/tourStory/tourStoryRepository';
import { TourStoryPageHeader } from '../../organisms/tour-story/TourStoryPageHeader';
import { TourStoryTable } from '../../organisms/tour-story/TourStoryTable';
import { FormTourStoryModal } from '../../organisms/tour-story/FormTourStoryModal';
import { appStyles } from '../../../styles';
import { MESSAGE_ERROR } from '../../../constants/message';
import { TourStoryType, TourStoryURLType } from '../../../types/tourStory';
import { TOUR_STORIES } from '../../../constants/define/translation';

export const TourStories = () => {
  const [tourStories, setTourStories] = useState<TourStoryType[]>([]);
  const [pageActive, setPageActive] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [sortOrderMax, setSortOrderMax] = useState(1);
  const [record, setDataRecord] = useState<any>();
  const { t } = useTranslation(TOUR_STORIES);

  const handleData = (datas: any) => {
    let addFakeData = 0;
    const currentCount = datas.length || 0;
    const newData = datas || [];
    const desiredMultiple = 10;
    const n = currentCount / desiredMultiple;
    const roundedCount = Math.ceil(n) * desiredMultiple;
    if (!currentCount) {
      addFakeData = 10;
    } else if (n % 1 === 0) {
      addFakeData = roundedCount + desiredMultiple - currentCount;
    } else {
      addFakeData = roundedCount - currentCount;
    }
    for (let i = 0; i < addFakeData; i += 1) {
      newData.push({ isSwitchVideo: true });
    }
    return newData.map((item: any, index: number) => ({ ...item, index }));
  };

  const handleGetTourStory = useCallback(async () => {
    try {
      setLoading(true);
      const stories = await getTourStoryListRepository({
        page: 1,
        take: PER_PAGE_100,
        country: 'USA',
      });

      if (stories?.tourStories.length === 0) {
        setTourStories(handleData([]));
        setLoading(false);
        return;
      }
      const data = handleData(stories?.tourStories);

      setTourStories(data);
      setLoading(false);
    } catch (e) {
      setTourStories(handleData([]));
      setLoading(false);
      message.error(MESSAGE_ERROR.M03);
    }
  }, []);

  const onCloseModal = useCallback(() => {
    setIsOpenModal(false);
  }, []);

  const onOpenModal = (index: number) => {
    const newData = [...tourStories];
    const item = newData.find(val => val?.index === index);
    if (item) {
      setDataRecord(item);
      setIsOpenModal(true);
    }
  };

  const handleChangePageActive = useCallback(value => {
    setPageActive(value);
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    handleGetTourStory();
  }, [handleGetTourStory]);

  useEffect(() => {
    if (!isEmpty(tourStories)) {
      const item = maxBy(tourStories, 'sortOrder');
      if (item && item?.sortOrder) {
        setSortOrderMax(item.sortOrder + 1);
      }
    }
  }, [tourStories]);

  const handleChangeSwitchVideo = (index: number) => {
    const newData = [...tourStories];
    const item = newData.find(val => val?.index === index);
    if (item) {
      newData[index].isSwitchVideo = !item?.isSwitchVideo;
    }
    setTourStories(newData);
  };

  const handleChangeInputVideo = (index: number, value: string) => {
    const newData = [...tourStories];
    const item = newData.find(val => val?.index === index);
    if (item) {
      newData[index].url = value;
    }
    setTourStories(newData);
  };

  const removeItemByIndex = (index: number) => {
    const newData = [...tourStories];
    const item = newData.find(val => val?.index === index);
    if (item) {
      newData.splice(index, 1);
    }

    setTourStories(newData);
  };

  const handleAddVideo = (index: number) => {
    const newData = [...tourStories];
    const item = newData.find(val => val?.index === index);
    if (item) {
      const url = item?.url;
      if (!url) {
        message.error('Please insert the youtube link of the Tour Story');
        return;
      }
      const isValidUrl = isValidYouTubeUrl(url);
      if (!isValidUrl) {
        message.error('Please insert the youtube link of the Tour Story');
        return;
      }
      try {
        setLoading(true);
        const payloadToPost = {
          type: TourStoryURLType.VIDEO,
          url,
          options: {},
          countries: 'USA',
          sortOrder: sortOrderMax,
        };

        postCreateTourStoryRepository(payloadToPost)
          .then(() => {
            handleGetTourStory();
            message.success(t('message.success.createTourStory'));
          })
          .catch(e => {
            handleGetTourStory();
            message.error(e?.errorMessage);
          });
        return;
      } catch (err) {
        setLoading(false);
        message.error(t('message.error.createTourStory'));
      }
    }
  };

  const isValidYouTubeUrl = (url: string) => {
    if (!url) return false;
    const regex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    return regex.test(url);
  };

  return (
    <Container>
      <TourStoryPageHeader />
      {(!tourStories || !tourStories.length) && (
        <Container>
          <Skeleton active />
        </Container>
      )}
      {tourStories && tourStories.length && (
        <TourStoryTable
          handleChangePageActive={handleChangePageActive}
          handleChangeSwitchVideo={handleChangeSwitchVideo}
          handleChangeInputVideo={handleChangeInputVideo}
          handleAddVideo={handleAddVideo}
          removeItemByIndex={removeItemByIndex}
          onRefresh={handleGetTourStory}
          records={tourStories || []}
          loading={loading}
          setLoading={setLoading}
          setDataRecord={setDataRecord}
          onOpenModal={onOpenModal}
          pagination={{
            onChange: handleChangePageActive,
            pageSize: PER_PAGE_100,
            current: pageActive,
            total: tourStories?.length,
          }}
        />
      )}

      {isOpenModal && (
        <FormTourStoryModal
          setDataRecord={setDataRecord}
          record={record}
          onRefresh={handleGetTourStory}
          isOpenModal={isOpenModal}
          onClose={onCloseModal}
          sortOrderMax={sortOrderMax}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .ant-pagination {
    float: right;
    padding: 15px 0 0 0;
  }

  .ant-table-thead {
    border-bottom: 2px solid #eee;
  }
  .ant-table-thead > tr > th {
    ${appStyles.background.tableBgGray}
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    &:before {
      display: none;
    }
  }

  & .ant-page-header {
    padding-left: 0px;
  }
`;
